/**
 * useOutreachFeed — "you've been given an outreach list"
 * (docs/crm-outreach-lists-plan.md §B7).
 *
 * NO POLL OF ITS OWN. The sidebar badge already asks GET /worklist?counts=true
 * every 60s, and that response carries `outreach.lists` — every active list I
 * am a recruiter on, with how many of its people are mine and open. This feed
 * reads that same answer through the shared nav counts, so a list launched a
 * minute ago reaches its recruiters on the next tick with zero extra requests.
 *
 * One card and one OS notification per list, per person, ever — the rules and
 * the wording live in outreachNotice.ts (pure, tested). The card does not
 * expire; "Work through" starts the list in the Next-up bar, and when nobody
 * on it can be called right now it opens the list page instead, which says
 * when the next tries start.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useSharedNavCounts } from '../../useNavCounts';
import { useWorkQueue } from '../../WorkQueueContext';
import { listHref } from '../../workQueueLogic';
import { fireOsNote } from './alertKinds';
import {
  assignmentLine,
  noticeStoreKey,
  parseNoticeStore,
  pickAssignments,
  serializeNoticeStore,
  withIds,
  type NoticeStore,
} from './outreachNotice';

export interface OutreachAssignedCard {
  /** `outreach:<listId>` — stable, so the queue keeps the card's served time. */
  id: string;
  listId: string;
  listName: string;
  /** "Luke gave you 106 people for the Diabetic Neuropathy Study". */
  line: string;
  launchedAt: string | null;
}

export interface OutreachFeed {
  cards: OutreachAssignedCard[];
  dismiss: (id: string) => void;
  open: (card: OutreachAssignedCard) => void;
}

const CARD_PREFIX = 'outreach:';

// localStorage can throw (private windows, blocked site data). The cost of a
// failure is one more card or one more notification — never a broken corner.
function readStore(userId: string | null): NoticeStore {
  if (!userId) return { notified: [], dismissed: [] };
  try { return parseNoticeStore(window.localStorage.getItem(noticeStoreKey(userId))); } catch { return { notified: [], dismissed: [] }; }
}
function writeStore(userId: string | null, s: NoticeStore): void {
  if (!userId) return;
  try { window.localStorage.setItem(noticeStoreKey(userId), serializeNoticeStore(s)); } catch { /* in-memory still holds */ }
}

export function useOutreachFeed(): OutreachFeed {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { startList } = useWorkQueue();
  const { outreach } = useSharedNavCounts();
  const userId = user?.id ?? null;
  const viewerName = user?.full_name ?? null;

  const [store, setStore] = useState<NoticeStore>(() => readStore(userId));
  // A different person signing in on this browser has their own "seen" set.
  useEffect(() => { setStore(readStore(userId)); }, [userId]);

  const { cards: lists, toNotify } = useMemo(
    () => pickAssignments(userId ? outreach?.lists : null, store),
    [userId, outreach, store],
  );

  // The one OS notification per list. Recorded BEFORE the next render reads
  // the store again, so a re-render (or the next poll) cannot fire it twice;
  // the per-list tag also collapses it across open tabs.
  useEffect(() => {
    if (!userId || !toNotify.length) return;
    for (const l of toNotify) {
      fireOsNote({ title: 'New outreach list', body: assignmentLine(l, viewerName), tag: `crm-outreach-assigned:${l.id}` });
    }
    const next = withIds(store, 'notified', toNotify.map((l) => l.id));
    writeStore(userId, next);
    setStore(next);
  }, [toNotify, userId, viewerName, store]);

  const cards = useMemo<OutreachAssignedCard[]>(() => lists.map((l) => ({
    id: `${CARD_PREFIX}${l.id}`,
    listId: l.id,
    listName: l.name,
    line: assignmentLine(l, viewerName),
    launchedAt: l.launched_at,
  })), [lists, viewerName]);

  const dismiss = useCallback((id: string) => {
    const listId = id.startsWith(CARD_PREFIX) ? id.slice(CARD_PREFIX.length) : id;
    setStore((prev) => {
      const next = withIds(prev, 'dismissed', [listId]);
      writeStore(userId, next);
      return next;
    });
  }, [userId]);

  const open = useCallback((card: OutreachAssignedCard) => {
    dismiss(card.id);
    void startList(card.listId).then((n) => {
      // Nobody callable right now: the list page says when the next tries start.
      if (n === 0) navigate(listHref(card.listId));
    });
  }, [dismiss, startList, navigate]);

  return { cards, dismiss, open };
}

export default useOutreachFeed;
